import {
  type InviteAcceptanceState,
  SharingError,
  stashInviteToken,
  takeStashedInviteToken,
  useInviteAcceptance,
} from '@jbroll/rowboat-sharing-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { clearOAuthError, readOAuthError } from '@/lib/authError';
import { ANON_IDENTITY, signOut, usePort, useRowboat, useSelect, useSession } from '@/rowboat';
import { setCaptureContext } from '@/services/captureContext';
import { listEstates } from '@/services/estateService';
import { roleName } from './ShareDrawer';
import { SignInForm } from './SignInForm';
import { Alert, Button, Card, Muted, Page } from './ui';

// One sync round brings the project's rows; past this the user has likely moved on.
const ARRIVAL_WAIT_MS = 30_000;

function Shell({ children }: { children: ReactNode }) {
  return (
    <main className="min-h-dvh bg-surface-secondary">
      <div className="border-b border-amber-200 bg-amber-50">
        <div className="mx-auto max-w-md px-4 py-2.5 text-base font-bold text-amber-900">
          RosaHouse
        </div>
      </div>
      <Page>
        <Card label="Invite">{children}</Card>
      </Page>
    </main>
  );
}

function Heading({ children }: { children: ReactNode }) {
  return <h1 className="mb-2 text-xl font-bold text-content-primary">{children}</h1>;
}

/** The page at `/invite/<token>`: sign in if needed, then accept or decline. */
export function InviteAccept({
  token,
  onAccepted,
  onLeave,
}: {
  token: string;
  onAccepted: (targetGroupId: string) => void;
  onLeave: () => void;
}) {
  const { data, isPending } = useSession();
  const email = data?.user.email ?? null;
  const { state, accept } = useInviteAcceptance({
    token,
    isAuthenticated: !!data,
    sessionPending: isPending,
  });

  // A token stashed for an OAuth round trip is spent once this page is open; left behind, it
  // would bring the user back here after accepting.
  useEffect(() => {
    takeStashedInviteToken();
  }, []);
  const [oauthError] = useState(readOAuthError);
  useEffect(clearOAuthError, []);

  // Kept through `accepting`, which carries no details.
  const details = useRef<Extract<InviteAcceptanceState, { status: 'valid' }> | null>(null);
  if (state.status === 'valid') details.current = state;

  const onAcceptedRef = useRef(onAccepted);
  onAcceptedRef.current = onAccepted;
  const acceptedGroup = state.status === 'accepted' ? state.targetGroupId : null;
  useEffect(() => {
    if (acceptedGroup) onAcceptedRef.current(acceptedGroup);
  }, [acceptedGroup]);

  const [signOutError, setSignOutError] = useState('');
  const otherAccount = (
    <>
      {email && <Muted>You are signed in as {email}.</Muted>}
      <div className="flex gap-2 pt-2">
        <Button
          onClick={() => {
            setSignOutError('');
            signOut().catch((err: Error) => setSignOutError(err.message));
          }}
        >
          Sign in with another account
        </Button>
        <Button variant="secondary" onClick={onLeave}>
          Go to RosaHouse
        </Button>
      </div>
      {signOutError && <Alert>{signOutError}</Alert>}
    </>
  );

  switch (state.status) {
    case 'loading':
    case 'accepted':
      return (
        <Shell>
          <Muted>Loading…</Muted>
        </Shell>
      );
    case 'not_authenticated':
      return (
        <Shell>
          <Heading>You have an invite</Heading>
          <div className="mb-3">
            <Muted>Sign in or sign up with the email address the invite was sent to.</Muted>
          </div>
          {/* The verification link often opens in another tab, out of reach of a stash, so it
              carries the token in its path. Google sign-in carries it the same way only when
              the browser refuses the stash. */}
          <SignInForm
            oauthCallbackURL={() => (stashInviteToken(token) ? '/' : `/invite/${token}`)}
            verifyCallbackURL={`/invite/${token}`}
            oauthErrorURL={`/invite/${token}`}
            initialError={oauthError}
          />
        </Shell>
      );
    case 'valid':
    case 'accepting': {
      const busy = state.status === 'accepting';
      const invite = details.current;
      return (
        <Shell>
          <Heading>You have an invite</Heading>
          {invite && (
            <p className="text-base text-content-primary">
              {invite.inviter.email} invited you to {invite.targetName ?? 'a project'} as{' '}
              {roleName(invite.role)}.
            </p>
          )}
          <div className="flex gap-2 pt-3">
            <Button disabled={busy} onClick={() => void accept()}>
              {busy ? 'Accepting…' : 'Accept'}
            </Button>
            <Button variant="secondary" disabled={busy} onClick={onLeave}>
              Decline
            </Button>
          </div>
        </Shell>
      );
    }
    case 'wrong_account':
      return (
        <Shell>
          <Heading>This invite is for another account</Heading>
          {otherAccount}
        </Shell>
      );
    case 'error': {
      if (state.reason === 'invalid') {
        return (
          <Shell>
            <Heading>This invite link is no longer valid.</Heading>
            {otherAccount}
          </Shell>
        );
      }
      if (state.reason === 'undo_pending') {
        return (
          <Shell>
            <Heading>Try this invite again in a minute</Heading>
            <Muted>
              RosaHouse is still undoing an earlier invite to this project for your account. This
              invite is still valid. If this keeps happening, ask an admin of the project to remove
              you from it and invite you again.
            </Muted>
            <div className="flex gap-2 pt-2">
              <Button onClick={() => window.location.reload()}>Try again</Button>
              <Button variant="secondary" onClick={onLeave}>
                Go to RosaHouse
              </Button>
            </div>
          </Shell>
        );
      }
      const refusal = refusalText(state.error);
      if (refusal) {
        return (
          <Shell>
            <Heading>This invite can't be accepted</Heading>
            <Muted>{refusal}</Muted>
            <div className="flex gap-2 pt-2">
              <Button variant="secondary" onClick={onLeave}>
                Go to RosaHouse
              </Button>
            </div>
          </Shell>
        );
      }
      return (
        <Shell>
          <Heading>Could not open the invite</Heading>
          <Muted>{failureText(state.error)}</Muted>
          <div className="flex gap-2 pt-2">
            <Button onClick={() => window.location.reload()}>Try again</Button>
            <Button variant="secondary" onClick={onLeave}>
              Go to RosaHouse
            </Button>
          </div>
        </Shell>
      );
    }
  }
}

// Accept refusals that trying again cannot fix: the invite itself must change.
const REFUSALS: Record<string, string> = {
  inviter_no_longer_admin:
    'The person who invited you can no longer share this project. Ask an admin of the project for a new invite.',
  grant_refused:
    'RosaHouse can no longer share this project. Ask an admin of the project for a new invite.',
  blocked:
    'Your account has blocked the account this invite comes from. Unblock it, or ask for an invite from someone else.',
  unknown_role:
    'This invite gives a role RosaHouse no longer has. Ask an admin of the project for a new invite.',
  root_group: "This invite is for your account's own group, which can't be shared.",
};

function refusalText(error: Error | null): string | null {
  if (!(error instanceof SharingError) || error.code === null) return null;
  return REFUSALS[error.code] ?? null;
}

function failureText(error: Error | null): string {
  if (error instanceof SharingError) {
    if (error.status === 401) return 'Your session ended. Sign in again, then open the link.';
    return `The server could not complete the request (${error.status}). Try again.`;
  }
  if (error instanceof TypeError) {
    return 'Could not reach the server. Check the connection and try again.';
  }
  // A malformed answer from validate, or a code the invite hook does not know.
  return `The server sent an answer RosaHouse can't read. Try again. (${error ? error.message : 'no error was reported'})`;
}

/**
 * After an accept, selects the project in `targetGroupId` once its row arrives, which takes one
 * sync round. Gives up after `ARRIVAL_WAIT_MS`.
 */
export function OpenAcceptedProject({
  targetGroupId,
  onDone,
}: {
  targetGroupId: string;
  onDone: () => void;
}) {
  const graph = useRowboat();
  const { author } = usePort();
  const estateId = useSelect(
    () => listEstates(graph).find((e) => e.owner_group_id === targetGroupId)?.id ?? null,
  );
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    const id = window.setTimeout(() => onDoneRef.current(), ARRIVAL_WAIT_MS);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!estateId) return;
    void setCaptureContext(graph, author ?? ANON_IDENTITY, { estateId })
      .catch((err: unknown) => console.error('[invite] could not select the project:', err))
      .finally(() => onDoneRef.current());
  }, [graph, author, estateId]);

  return null;
}
